import { useState } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { InviteLinkBox, useAuth } from "@workspace/admin-ui";
import { PageHeader } from "@/components/AppShell";
import {
  pharmacyAdminApi,
  type InviteRow,
  type MembershipRow,
} from "@/lib/callables";
import { useToast } from "@/lib/toast";

function BackButton() {
  return (
    <Button component={Link} href="/" startIcon={<ArrowBackIcon />}>
      Back to pharmacists
    </Button>
  );
}

function MemberDetail({ member, onChange }: { member: MembershipRow; onChange: () => void }) {
  const { user } = useAuth();
  const isSelf = !!user && member.uid === user.uid;
  const { showSuccess, showError, showWarning } = useToast();
  const [confirming, setConfirming] = useState<"revoke" | "suspend" | "reactivate" | null>(null);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [editName, setEditName] = useState(member.displayName);
  const [editPsi, setEditPsi] = useState("");
  const isSuspended = member.status === "suspended";

  const updateM = useMutation({
    mutationFn: () =>
      pharmacyAdminApi.updatePharmacist({
        membershipId: member.membershipId,
        displayName: editName.trim() || undefined,
        psiNumber: editPsi.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess(`Updated ${member.email}`);
      setEditing(false);
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const revokeM = useMutation({
    mutationFn: () =>
      pharmacyAdminApi.revokeMembership({
        membershipId: member.membershipId,
        reason: reason.trim() || undefined,
        disableAuth: true,
      }),
    onSuccess: (res) => {
      showSuccess(`Revoked ${member.email}`);
      if (res.authError) showWarning(`Auth disable: ${res.authError}`);
      setConfirming(null);
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const suspendM = useMutation({
    mutationFn: () =>
      pharmacyAdminApi.suspendMembership({
        membershipId: member.membershipId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess(`Suspended ${member.email}`);
      setConfirming(null);
      setReason("");
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const reactivateM = useMutation({
    mutationFn: () =>
      pharmacyAdminApi.reactivateMembership({ membershipId: member.membershipId }),
    onSuccess: () => {
      showSuccess(`Reactivated ${member.email}`);
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  return (
    <>
      <PageHeader
        title={member.displayName || member.email || "Pharmacist"}
        description={member.email}
        actions={<BackButton />}
      />
      <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Profile
            </Typography>
            {isSuspended ? (
              <Chip label="Suspended" size="small" color="warning" />
            ) : (
              <Chip label="Active" size="small" color="success" variant="outlined" />
            )}
          </Stack>

          {!editing ? (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">Full name</Typography>
                <Typography variant="body2">{member.displayName || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body2">{member.email || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">PSI number</Typography>
                <Typography variant="body2">{member.psiNumber || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">User ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{member.uid}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Member since</Typography>
                <Typography variant="body2">
                  {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : "—"}
                </Typography>
              </Box>
              {isSuspended ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">Suspended</Typography>
                  <Typography variant="body2">
                    {member.suspendedAt ? new Date(member.suspendedAt).toLocaleDateString() : "—"}
                    {member.suspendedReason ? ` — ${member.suspendedReason}` : ""}
                  </Typography>
                </Box>
              ) : null}
              <Box sx={{ pt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setEditing(true);
                    setEditName(member.displayName);
                    setEditPsi("");
                  }}
                >
                  Edit details
                </Button>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <TextField
                  label="Full name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="PSI number (leave blank to keep)"
                  helperText="5 or 6 digits"
                  value={editPsi}
                  onChange={(e) => setEditPsi(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Box>
              <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                <Button variant="text" size="small" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => updateM.mutate()}
                  disabled={updateM.isPending || (!editName.trim() && !editPsi.trim())}
                >
                  {updateM.isPending ? "Saving…" : "Save changes"}
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Access
          </Typography>

          {!confirming ? (
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {!isSelf ? (
                isSuspended ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setConfirming("reactivate")}
                    disabled={reactivateM.isPending}
                  >
                    Reactivate
                  </Button>
                ) : (
                  <Button size="small" variant="outlined" onClick={() => setConfirming("suspend")}>
                    Suspend
                  </Button>
                )
              ) : null}
              <Button size="small" variant="outlined" color="error" onClick={() => setConfirming("revoke")}>
                Revoke access
              </Button>
            </Stack>
          ) : confirming === "reactivate" ? (
            <Box sx={{ p: 2, borderRadius: 1, border: 1, borderColor: "primary.light", bgcolor: "primary.50" }}>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Reactivate <strong>{member.email}</strong>? They will regain access to this pharmacy.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                <Button variant="text" size="small" onClick={() => setConfirming(null)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    reactivateM.mutate();
                    setConfirming(null);
                  }}
                  disabled={reactivateM.isPending}
                >
                  {reactivateM.isPending ? "Reactivating…" : "Confirm reactivate"}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ p: 2, borderRadius: 1, border: 1, borderColor: "error.light", bgcolor: "error.50" }}>
              <Stack spacing={2}>
                <TextField
                  label={
                    confirming === "revoke"
                      ? "Reason (recorded permanently)"
                      : "Reason (recorded on membership)"
                  }
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  rows={2}
                  placeholder={
                    confirming === "revoke" ? "e.g. Left the pharmacy" : "e.g. On extended leave"
                  }
                  size="small"
                  fullWidth
                />
                <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setConfirming(null);
                      setReason("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => (confirming === "revoke" ? revokeM.mutate() : suspendM.mutate())}
                    disabled={revokeM.isPending || suspendM.isPending}
                  >
                    {confirming === "revoke"
                      ? revokeM.isPending
                        ? "Revoking…"
                        : "Confirm revoke"
                      : suspendM.isPending
                        ? "Suspending…"
                        : "Confirm suspend"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            History
          </Typography>
          <HistoryTimeline
            entries={[
              {
                label: "Membership created",
                at: member.createdAt,
              },
              ...(member.suspendedAt
                ? [
                    {
                      label: "Suspended",
                      at: member.suspendedAt,
                      detail: member.suspendedReason || undefined,
                    },
                  ]
                : []),
            ]}
          />
        </Paper>
      </Box>
    </>
  );
}

function HistoryTimeline({
  entries,
}: {
  entries: { label: string; at: string | null; detail?: string }[];
}) {
  const sorted = [...entries].sort((a, b) => {
    if (!a.at && !b.at) return 0;
    if (!a.at) return 1;
    if (!b.at) return -1;
    return new Date(b.at).getTime() - new Date(a.at).getTime();
  });
  return (
    <Stack spacing={1.5}>
      {sorted.map((e, i) => (
        <Box
          key={`${e.label}-${i}`}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            pb: i < sorted.length - 1 ? 1.5 : 0,
            borderBottom: i < sorted.length - 1 ? 1 : 0,
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography variant="body2">{e.label}</Typography>
            {e.detail ? (
              <Typography variant="caption" color="text.secondary">
                {e.detail}
              </Typography>
            ) : null}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {e.at ? new Date(e.at).toLocaleString() : "—"}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function InviteDetail({ invite, onDismissed }: { invite: InviteRow; onDismissed: () => void }) {
  const { showError } = useToast();
  const [, setLocation] = useLocation();
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmingDismiss, setConfirmingDismiss] = useState(false);

  const resendM = useMutation({
    mutationFn: () => pharmacyAdminApi.resendInvite({ inviteId: invite.id }),
    onSuccess: (res) => setLink(res.inviteLink),
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const dismissM = useMutation({
    mutationFn: () => pharmacyAdminApi.dismissInvite({ inviteId: invite.id }),
    onSuccess: () => {
      onDismissed();
      setLocation("/");
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const copy = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      <PageHeader
        title={invite.displayName || invite.email || "Pending invite"}
        description={invite.email}
        actions={<BackButton />}
      />
      <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Profile
            </Typography>
            <Chip label="Invited — password not yet set" size="small" color="info" variant="outlined" />
          </Stack>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Full name</Typography>
              <Typography variant="body2">{invite.displayName || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography variant="body2">{invite.email || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Invited</Typography>
              <Typography variant="body2">
                {invite.createdAt ? new Date(invite.createdAt).toLocaleDateString() : "—"}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            History
          </Typography>
          <HistoryTimeline entries={[{ label: "Invite created", at: invite.createdAt }]} />
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Setup link
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: link ? 0 : 0 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => resendM.mutate()}
              disabled={resendM.isPending}
            >
              {resendM.isPending ? "Generating…" : "Get new link"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => setConfirmingDismiss(true)}
              disabled={dismissM.isPending}
            >
              {dismissM.isPending ? "Dismissing…" : "Dismiss invite"}
            </Button>
          </Stack>
          {confirmingDismiss ? (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                border: 1,
                borderColor: "error.light",
                bgcolor: "error.50",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Dismiss this invite for <strong>{invite.email || "this pharmacist"}</strong>? This
                cannot be undone.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                <Button variant="text" size="small" onClick={() => setConfirmingDismiss(false)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => {
                    setConfirmingDismiss(false);
                    dismissM.mutate();
                  }}
                  disabled={dismissM.isPending}
                >
                  {dismissM.isPending ? "Dismissing…" : "Confirm dismiss"}
                </Button>
              </Stack>
            </Box>
          ) : null}
          {link ? (
            <TextField
              value={link}
              size="small"
              fullWidth
              sx={{ mt: 2 }}
              slotProps={{
                input: {
                  readOnly: true,
                  sx: { fontFamily: "monospace", fontSize: "0.75rem" },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? "Copied!" : "Copy link"}>
                        <IconButton size="small" onClick={() => copy(link)}>
                          {copied ? (
                            <CheckIcon fontSize="small" color="success" />
                          ) : (
                            <ContentCopyIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          ) : null}
        </Paper>
      </Box>
    </>
  );
}

export default function PharmacistDetailPage() {
  const params = useParams<{ id: string }>();
  const raw = params.id ?? "";
  const sepIdx = raw.indexOf(":");
  const kind = sepIdx >= 0 ? raw.slice(0, sepIdx) : "";
  const entityId = sepIdx >= 0 ? decodeURIComponent(raw.slice(sepIdx + 1)) : "";
  const qc = useQueryClient();

  const membersQ = useQuery({
    queryKey: ["pharmacy-admin", "members", "all"],
    queryFn: () => pharmacyAdminApi.listPharmacists({ status: "all" }),
    enabled: kind === "member",
  });

  const invitesQ = useQuery({
    queryKey: ["pharmacy-admin", "invites"],
    queryFn: () => pharmacyAdminApi.listInvites(),
    enabled: kind === "invite",
  });

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ["pharmacy-admin", "members"] });
    void qc.invalidateQueries({ queryKey: ["pharmacy-admin", "invites"] });
  };

  if (kind === "member") {
    if (membersQ.isLoading) {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">Loading…</Typography>
        </Box>
      );
    }
    const member = membersQ.data?.items.find((m) => m.membershipId === entityId);
    if (!member) {
      return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <PageHeader title="Pharmacist not found" actions={<BackButton />} />
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              This pharmacist could not be found. They may have been removed.
            </Typography>
          </Box>
        </Box>
      );
    }
    return <MemberDetail member={member} onChange={invalidateAll} />;
  }

  if (kind === "invite") {
    if (invitesQ.isLoading) {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">Loading…</Typography>
        </Box>
      );
    }
    const invite = invitesQ.data?.items.find((i) => i.id === entityId);
    if (!invite) {
      return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <PageHeader title="Invite not found" actions={<BackButton />} />
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              This invite is no longer pending. It may have been dismissed or completed.
            </Typography>
          </Box>
        </Box>
      );
    }
    return <InviteDetail invite={invite} onDismissed={invalidateAll} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <PageHeader title="Not found" actions={<BackButton />} />
    </Box>
  );
}
